const BIN_WIDTH = 2;

const TEXT_PRIMARY = '#e2e8f0';
const TEXT_TERTIARY = '#64748b';
const BORDER_TERTIARY = '#475569';

const W = 640;
const H = 220;
const PAD_L = 40;
const PAD_R = 40;
const CHART_W = W - PAD_L - PAD_R;
const BASELINE_Y = 170;
const CURVE_H = 120;

export function computeStats(weeklyData, binMax) {
  if (!Array.isArray(weeklyData)) return null;
  const pts = [];
  for (const entry of weeklyData) {
    // Guard against NaN/Infinity (e.g. a saved league missing scoring keys) so the
    // binning below never hits `new Array(NaN)` → "Invalid array length".
    if (entry && typeof entry.points === 'number' && Number.isFinite(entry.points)) pts.push(entry.points);
  }
  if (pts.length === 0) return null;

  const sorted = [...pts].sort((a, b) => a - b);
  const n = sorted.length;
  const min = sorted[0];
  const max = sorted[n - 1];
  const median = n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;

  const effectiveBinMax = binMax != null && binMax > 0 ? binMax : Math.max(BIN_WIDTH, Math.ceil(max / 5) * 5);
  const numBins = Math.max(1, Math.ceil(effectiveBinMax / BIN_WIDTH));
  const counts = new Array(numBins).fill(0);
  for (const v of pts) {
    if (v < 0) continue;
    const idx = Math.min(numBins - 1, Math.floor(v / BIN_WIDTH));
    counts[idx]++;
  }

  const smoothed = counts.map((c, i) => {
    const left = i > 0 ? counts[i - 1] : c;
    const right = i < counts.length - 1 ? counts[i + 1] : c;
    return (left + c + right) / 3;
  });

  const bins = smoothed.map((smoothedCount, i) => ({
    center: i * BIN_WIDTH + BIN_WIDTH / 2,
    smoothedCount,
  }));

  return { n, min, max, median, bins };
}

function curveYAtValue(value, bins, xFor, yFor) {
  if (!bins || bins.length === 0) return BASELINE_Y;
  const centers = bins.map(b => b.center);
  if (value <= centers[0]) return yFor(bins[0].smoothedCount);
  if (value >= centers[centers.length - 1]) return yFor(bins[bins.length - 1].smoothedCount);
  for (let i = 0; i < centers.length - 1; i++) {
    if (value >= centers[i] && value <= centers[i + 1]) {
      const t = (value - centers[i]) / (centers[i + 1] - centers[i]);
      const count = bins[i].smoothedCount + t * (bins[i + 1].smoothedCount - bins[i].smoothedCount);
      return yFor(count);
    }
  }
  return BASELINE_Y;
}

function buildDensityPath(stats, xFor, yFor) {
  const { min, max, bins } = stats;
  const points = bins.map(b => ({ x: xFor(b.center), y: yFor(b.smoothedCount) }));
  if (points.length === 0) return '';

  const parts = [`M ${xFor(min)} ${BASELINE_Y}`];

  if (points.length === 1) {
    parts.push(`L ${points[0].x} ${points[0].y}`);
  } else {
    const m0x = (points[0].x + points[1].x) / 2;
    const m0y = (points[0].y + points[1].y) / 2;
    parts.push(`L ${points[0].x} ${points[0].y}`);
    parts.push(`L ${m0x} ${m0y}`);
    for (let i = 1; i < points.length - 1; i++) {
      const mx = (points[i].x + points[i + 1].x) / 2;
      const my = (points[i].y + points[i + 1].y) / 2;
      parts.push(`Q ${points[i].x} ${points[i].y} ${mx} ${my}`);
    }
    parts.push(`L ${points[points.length - 1].x} ${points[points.length - 1].y}`);
  }

  parts.push(`L ${xFor(max)} ${BASELINE_Y}`);
  parts.push('Z');
  return parts.join(' ');
}

export default function RangeOfOutcomes({ players, maxGamesPerPlayer, hideLegend = false }) {
  if (!Array.isArray(players) || players.length === 0) return null;

  const prelim = players.map(p => {
    const data = Array.isArray(p.weeklyData) && maxGamesPerPlayer > 0 && p.weeklyData.length > maxGamesPerPlayer
      ? p.weeklyData.slice(-maxGamesPerPlayer)
      : p.weeklyData;
    const pts = Array.isArray(data) ? data.filter(e => e && typeof e.points === 'number').map(e => e.points) : [];
    return { player: p, data, playerMax: pts.length > 0 ? Math.max(...pts) : null };
  });

  const validMaxes = prelim.map(p => p.playerMax).filter(v => typeof v === 'number');
  if (validMaxes.length === 0) return null;

  const xMax = Math.max(5, Math.ceil(Math.max(...validMaxes) / 5) * 5);

  const rows = prelim.map(({ player, data }) => ({
    player,
    stats: computeStats(data, xMax),
  }));

  let yMax = 0;
  for (const r of rows) {
    if (!r.stats) continue;
    for (const b of r.stats.bins) {
      if (b.smoothedCount > yMax) yMax = b.smoothedCount;
    }
  }
  if (yMax <= 0) yMax = 1;

  const xFor = (v) => PAD_L + (v / xMax) * CHART_W;
  const yFor = (count) => BASELINE_Y - (count / yMax) * CURVE_H;

  const tickStep = xMax <= 25 ? 5 : xMax <= 50 ? 10 : 20;
  const ticks = [];
  for (let t = 0; t <= xMax; t += tickStep) ticks.push(t);

  return (
    <div>
      {!hideLegend && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 8 }}>
          {rows.map((row, i) => {
            const c = row.player.color;
            const s = row.stats;
            const statsText = s
              ? `typical ${s.median.toFixed(1)} · range ${s.min.toFixed(1)}–${s.max.toFixed(1)} · ${s.n}g`
              : 'no data';
            return (
              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontFamily: 'Figtree, sans-serif' }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: c, flexShrink: 0 }} />
                <span style={{ color: TEXT_PRIMARY, fontWeight: 500 }}>{row.player.name}</span>
                <span style={{ color: TEXT_TERTIARY }}>{statsText}</span>
              </div>
            );
          })}
        </div>
      )}

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        <line x1={PAD_L} x2={W - PAD_R} y1={BASELINE_Y} y2={BASELINE_Y} stroke={BORDER_TERTIARY} strokeWidth="1" />

        {rows.map((row, i) => {
          if (!row.stats) return null;
          const c = row.player.color;
          const s = row.stats;
          const d = buildDensityPath(s, xFor, yFor);
          return (
            <path key={`curve-${i}`} d={d} fill={c} fillOpacity="0.2" stroke={c} strokeWidth="1.5" strokeLinejoin="round" />
          );
        })}

        {rows.map((row, i) => {
          if (!row.stats) return null;
          const c = row.player.color;
          const s = row.stats;
          const mx = xFor(s.median);
          const myCurve = curveYAtValue(s.median, s.bins, xFor, yFor);
          return (
            <g key={`median-${i}`}>
              <line x1={mx} x2={mx} y1={BASELINE_Y} y2={myCurve} stroke={c} strokeWidth="1" strokeDasharray="2,2" opacity="0.6" />
              <circle cx={mx} cy={BASELINE_Y} r={4} fill={c} />
            </g>
          );
        })}

        {rows.map((row, i) => {
          if (!row.stats) return null;
          const c = row.player.color;
          const s = row.stats;
          return (
            <g key={`extrema-${i}`}>
              <rect x={xFor(s.min) - 2} y={BASELINE_Y - 3} width={4} height={6} fill={c} opacity="0.7" />
              <rect x={xFor(s.max) - 2} y={BASELINE_Y - 3} width={4} height={6} fill={c} opacity="0.7" />
            </g>
          );
        })}

        {ticks.map(t => (
          <g key={`tick-${t}`}>
            <line x1={xFor(t)} x2={xFor(t)} y1={BASELINE_Y} y2={BASELINE_Y + 4} stroke={BORDER_TERTIARY} strokeWidth="1" />
            <text x={xFor(t)} y={BASELINE_Y + 16} textAnchor="middle" fontSize="10" fill={TEXT_TERTIARY} fontFamily="Figtree, sans-serif">{t}</text>
          </g>
        ))}
      </svg>

      <div style={{ fontSize: 10, color: TEXT_TERTIARY, fontStyle: 'italic', textAlign: 'center', marginTop: 4 }}>
        Points per game · taller = more common · wider = more volatile
      </div>
    </div>
  );
}
